/**
 * @file response.js
 * @description Utilitários para respostas da API (CRA)
 */

import { isAbortedListRequest } from "../../lib/is-aborted-list-request.js"
import { FetchError } from "../interceptors/error-handler.js"

/**
 * Cria uma resposta de sucesso padronizada
 * @param {*} data - Dados da resposta
 * @param {number} status - Status HTTP
 * @param {string} message - Mensagem de sucesso
 * @returns {Object} Resposta de sucesso formatada
 */
export function createSuccessResponse(data, status = 200, message = "Operação realizada com sucesso") {
  return {
    success: true,
    status,
    message,
    data,
  }
}

/**
 * Cria uma resposta de erro padronizada
 * @param {string} message - Mensagem de erro
 * @param {number} status - Status HTTP
 * @param {string} code - Código de erro
 * @param {*} details - Detalhes do erro
 * @returns {Object} Resposta de erro formatada
 */
export function createErrorResponse(message, status = 500, code = "UNKNOWN_ERROR", details) {
  return {
    success: false,
    status,
    message,
    code,
    details,
  }
}

/**
 * Trata e formata erros de API
 * @param {*} error - Erro da API
 * @returns {Object} Resposta de erro formatada
 */
export function handleApiError(error) {
  // Abort/cancel é fluxo normal (debounce, troca de filtro, unmount) — não poluir o console
  if (isAbortedListRequest(error)) {
    return {
      success: false,
      status: 0,
      message: error?.message || "Requisição cancelada",
      code: "ERR_CANCELED",
      error,
      aborted: true,
    }
  }

  console.error("❌ API Error:", error)

  if (error instanceof FetchError) {
    return {
      success: false,
      status: error.status,
      message: error.message,
      code: error.code,
      details: error.details,
      error,
    }
  }

  return {
    success: false,
    status: 500,
    message: error?.message || "Ocorreu um erro inesperado",
    code: "UNKNOWN_ERROR",
    error,
  }
}

/**
 * Verifica se a resposta é de sucesso
 * @param {Object} response - Resposta da API
 * @returns {boolean} true se a resposta é de sucesso
 */
export function isSuccessResponse(response) {
  return response.success === true
}

/**
 * Extrai mensagem de erro padronizada
 * @param {*} error - Objeto de erro
 * @param {string} fallback - Mensagem padrão
 * @returns {string} Mensagem do erro
 */
export function getErrorMessage(error, fallback = "Ocorreu um erro inesperado") {
  if (!error) {
    return fallback
  }

  if (error instanceof FetchError) {
    return error.message || fallback
  }

  if (typeof error === "string") {
    return error
  }

  if (typeof error.message === "string" && error.message.trim().length > 0) {
    return error.message
  }

  if (typeof error.error === "string" && error.error.trim().length > 0) {
    return error.error
  }

  return fallback
}

/**
 * Executa uma requisição API e retorna resposta padronizada
 * @param {Function} requestFn - Função assíncrona que executa a requisição
 * @param {string[]} [tags] - Tags opcionais para identificação/cache
 * @returns {Promise<Object>} Resposta padronizada de sucesso ou erro
 */
export async function handleRequest(requestFn, tags = []) {
  try {
    const response = await requestFn()
    const data = response?.data ?? response
    return createSuccessResponse(data, 200, "Operação realizada com sucesso")
  } catch (error) {
    return handleApiError(error)
  }
}

/**
 * Extrai código de erro padronizado
 * @param {*} error - Objeto de erro
 * @param {string} fallback - Código padrão
 * @returns {string} Código do erro
 */
export function getErrorCode(error, fallback = "UNKNOWN_ERROR") {
  if (!error) {
    return fallback
  }

  if (error instanceof FetchError) {
    return error.code || fallback
  }

  if (typeof error.code === "string" && error.code.trim().length > 0) {
    return error.code
  }

  if (typeof error.status === "number") {
    return `HTTP_${error.status}`
  }

  return fallback
}
